/*
 * Proprietary and confidential | 2023 | SoundTalks NV
 */

import * as Yup from 'yup';
import Ajv from 'ajv';

import alertRuleConditionSchema from 'constants/alert_rule_schema.json';

export const supportedImageFormats = ['image/jpg', 'image/jpeg', 'image/png'];
const nameRegEx = /^[\p{Letter}\p{Mark}\s-]+$/u;

// `discriminator` here is the OpenAPI-style keyword the backend's Pydantic export leaves in
// place for consumers that support it (conditions-builder reads it directly to build the UI);
// it's not a JSON Schema keyword ajv understands, so `strict: false` lets ajv skip over it and
// fall back to plain `oneOf` structural validation, which is all this check needs.
const ajv = new Ajv({ strict: false });
const validateConditionsSchema = ajv.compile(alertRuleConditionSchema);

export const LoginSchema = (t) =>
  Yup.object().shape({
    username: Yup.string()
      .email(t('error_invalid', { what: t('email_address') }))
      .required(t('error_required', { what: t('email') })),
    password: Yup.string()
      .min(8, t('error_longer', { what: t('password'), count: '8' }))
      .required(t('error_required', { what: t('password') })),
  });

export const ContactUsSchema = (t) =>
  Yup.object().shape({
    name: Yup.string().max(180, t('error_too_long')),
    email: Yup.string()
      .email(t('error_invalid', { what: t('email_address') }))
      .max(150, t('error_too_long'))
      .required(t('error_required', { what: t('email') })),
    message: Yup.string()
      .min(25, t('error_longer', { what: 'Message', count: '25' }))
      .required(t('error_required', { what: 'Message' })),
  });

export const RestorePasswordSchema = (t) =>
  Yup.object().shape({
    email: Yup.string()
      .email(t('error_invalid', { what: t('email_address') }))
      .required(t('error_required', { what: t('email') })),
  });

export const RecoverPasswordSchema = (t) =>
  Yup.object().shape({
    password: Yup.string()
      .min(8, t('error_longer', { what: t('password'), count: '8' }))
      .max(100, t('error_too_long'))
      .required(t('error_required', { what: t('password') })),
    confirm_password: Yup.string()
      .oneOf([Yup.ref('password'), null], t('error_match'))
      .required(t('error_required', { what: t('password_confirm') })),
  });

export const ProfileGeneralSchema = (t) =>
  Yup.object().shape({
    first_name: Yup.string()
      .min(2, t('error_longer', { what: t('first_name'), count: '2' }))
      .max(30, t('error_too_long'))
      .required(t('error_required', { what: t('first_name') }))
      .matches(nameRegEx, t('error_wrong_characters')),
    last_name: Yup.string()
      .max(150, t('error_too_long'))
      .required(t('error_required', { what: t('last_name') }))
      .matches(nameRegEx, t('error_wrong_characters')),
    email: Yup.string()
      .email(t('error_invalid', { what: t('email_address') }))
      .required(t('error_required', { what: t('email') })),
    telephone: Yup.string()
      .min(2, t('error_longer', { what: t('telephone'), count: '2' }))
      .max(20, t('error_too_long'))
      .required(t('error_required', { what: t('telephone') })),
    language: Yup.string().min(2, t('error_longer', { what: t('Language'), count: '4' })),
    timezone: Yup.string().min(2, t('error_longer', { what: t('timezone'), count: '2' })),
    temperature: Yup.string().min(2, t('error_longer', { what: t('temperature'), count: '2' })),
    profile_picture: Yup.mixed()
      .test('fileFormat', t('error_format'), (value) =>
        value && typeof value !== 'string' ? supportedImageFormats.includes(value.type) : true,
      )
      .test(
        'fileSize',
        t('error_too_large', { what: t('profile_picture') }),
        (value) => !value || typeof value === 'string' || value.size <= 5000000,
      ),
  });

export const GroupCreationSchema = (t) =>
  Yup.object().shape({
    name: Yup.string()
      .min(2, t('error_longer', { what: t('name'), count: '2' }))
      .max(250, t('error_too_long'))
      .required(t('error_required', { what: t('name') })),
    linked_sites: Yup.array().of(Yup.number()).min(1, t('error_add_site')).required(t('error_link_site')),
  });

export const SiteUsersSchema = (t) =>
  Yup.object().shape({
    email: Yup.string()
      .trim()
      .email(t('error_invalid', { what: t('email_address') }))
      .max(150, t('error_too_long'))
      .required(t('error_required', { what: t('email') })),
  });

export const SiteUsersWechatSchema = (t) =>
  Yup.object().shape({
    email: Yup.string()
      .trim()
      .max(150, t('error_too_long'))
      .required(t('error_required', { what: t('email_or_wechat_union_id') }))
      .test('email-or-wechat-union-id', t('error_invalid', { what: t('email_address') }), (value) => {
        if (!value) return false;
        if (value.includes('@')) {
          return Yup.string().email().isValidSync(value);
        }
        return true;
      }),
  });

export const WechatInvitationSchema = (t) =>
  Yup.object().shape({
    description: Yup.string()
      .trim()
      .max(500, t('error_too_long'))
      .required(t('error_required', { what: t('description') })),
  });

export const NotesEditSchema = (t) =>
  Yup.object().shape({
    title: Yup.string()
      .min(2, t('error_longer', { what: t('name'), count: '2' }))
      .max(50, t('error_too_long'))
      .required(t('error_required', { what: t('name') })),
    description: Yup.string()
      .min(2, t('error_longer', { what: t('description'), count: '2' }))
      .max(2000, t('error_too_long'))
      .required(t('error_required', { what: t('description') })),
  });

// export const SiteCreationSchemaFirst = t =>
//   Yup.object().shape({
//     name: Yup.string()
//       .min(2, t('error_longer', { what: t('name'), count: '2' }))
//       .max(250, t('error_too_long'))
//       .required(t('error_required', { what: t('name') })),
//     type: Yup.string().required(t('error_required_simple')),
//     animals_quantity: Yup.string()
//       .matches(/^[0-9]*$/, t('error_wrong_characters'))
//       .max(250, t('error_too_long')),
//   });

export const SiteCreationSchemaFirst = (t) =>
  Yup.object().shape({
    name: Yup.string()
      .min(2, t('error_longer', { what: t('name'), count: '2' }))
      .max(250, t('error_too_long'))
      .required(t('error_required', { what: t('name') })),
    country: Yup.string().nullable().required(t('error_required_simple')),
    // postal_code: Yup.string().matches(/^[a-zA-Z0-9 ]*$/, t('error_wrong_characters')),
    city: Yup.string().nullable(),
    // street: Yup.string(),
    // house_number: Yup.string().matches(/^[0-9]*$/, t('error_wrong_characters')),
    latitude: Yup.number().required(t('error_required_simple')),
    longitude: Yup.number().required(t('error_required_simple')),
    premise_id: Yup.string().max(50, t('error_too_long')),
  });

const NameSchema = (t) =>
  Yup.mixed().test('name', t('name_required'), (value) => !value || (typeof value === 'string' && value.trim()));

export const SiteCreationSchemaSecond = (t) =>
  Yup.object({
    houses: Yup.array()
      .of(
        Yup.object({
          name: NameSchema(t),
          children: Yup.array().of(
            Yup.object({
              name: NameSchema(t),
              children: Yup.array().of(
                Yup.object({
                  name: NameSchema(t),
                  children: Yup.array().of(
                    Yup.object({
                      name: NameSchema(t),
                    }),
                  ),
                }),
              ),
            }),
          ),
        }),
      )
      .required(t('error_required_simple')),
  });

export const SiteCreationSchemaThird = (t) =>
  Yup.object().shape({
    airspaces: Yup.array().of(
      Yup.object().shape({
        product_type: Yup.string().required(t('error_required_simple')),
      }),
    ),
    batch_profiles: Yup.array()
      .of(
        Yup.object().shape({
          product_type: Yup.string().required(t('error_required_simple')),
          typical_duration: Yup.number().when('product_type', {
            is: (pt) => pt === 'Boars' || pt === 'Gilt',
            then: Yup.number().nullable().notRequired(),
            otherwise: Yup.number().min(1, t('error_must_be_greater_than_0')).required(t('error_required_simple')),
          }),
        }),
      )
      .test('active-profiles-required', t('site_error_batch_profile_required'), function (profiles) {
        const { airspaces } = this.parent;
        if (!profiles || !airspaces) return true;

        // Create a mapping of active product types
        const activeProductTypes = {};
        airspaces.forEach((airspace) => {
          if (airspace.product_type) {
            activeProductTypes[airspace.product_type] = true;
          }
        });

        // Check if all active product types have valid profiles
        for (const productType of Object.keys(activeProductTypes)) {
          if (activeProductTypes[productType]) {
            // Skip Boars and Gilt from profile requirement
            if (productType === 'Boars' || productType === 'Gilt') {
              continue;
            }
            const profile = profiles.find((p) => p.product_type === productType);

            if (!profile) {
              return false;
            }

            // Skip validation on individual fields as they have their own validators
          }
        }

        return true;
      }),
  });

export const SiteCreationSchemaFourth = (t) =>
  Yup.object().shape({
    gateways: Yup.array().of(Yup.object()).required(t('error_required_simple')),
    houses: Yup.array()
      .of(
        Yup.object({
          children: Yup.array().of(
            Yup.object({
              children: Yup.array().of(
                Yup.object({
                  children: Yup.array().of(
                    Yup.object({
                      devices: Yup.array().of(Yup.object()).nullable().required(t('error_required_device')),
                    }),
                  ),
                }),
              ),
            }),
          ),
        }),
      )
      .required(t('error_required_simple')),
  });

export const RegistrationSchema = (t) =>
  Yup.object().shape({
    first_name: Yup.string()
      .trim()
      .min(1, t('error_longer', { what: t('first_name'), count: '1' }))
      .max(30, t('error_too_long'))
      .required(t('error_required', { what: t('first_name') }))
      .matches(nameRegEx, t('error_wrong_characters')),
    last_name: Yup.string()
      .trim()
      .min(1, t('error_longer', { what: t('last_name'), count: '1' }))
      .max(150, t('error_too_long'))
      .required(t('error_required', { what: t('last_name') }))
      .matches(nameRegEx, t('error_wrong_characters')),
    email: Yup.string()
      .email(t('error_invalid', { what: t('email_address') }))
      .max(150, t('error_too_long'))
      .required(t('error_required', { what: t('email') })),
    password: Yup.string()
      .min(8, t('error_longer', { what: t('password'), count: '8' }))
      .max(100, t('error_too_long'))
      .required(t('error_required', { what: t('password') })),
    telephone: Yup.string()
      .min(9, t('error_longer', { what: t('telephone'), count: '5' }))
      .max(20, t('error_too_long'))
      .required(t('error_required', { what: t('telephone') })),
    confirm_password: Yup.string()
      .oneOf([Yup.ref('password'), null], t('error_match'))
      .required(t('error_required', { what: t('password_confirm') })),
    timezone: Yup.string()
      .nullable()
      .required(t('error_required', { what: t('timezone') })),
    language: Yup.string()
      .nullable()
      .required(t('error_required', { what: t('language') })),
    country: Yup.string()
      .nullable()
      .required(t('error_required', { what: t('country') })),
  });

export const PasswordChangingSchema = (t) =>
  Yup.object().shape({
    old_password: Yup.string()
      .min(8, t('error_longer', { what: t('password'), count: '8' }))
      .max(100, t('error_too_long'))
      .required(t('error_required', { what: t('old_password') })),
    new_password: Yup.string()
      .min(8, t('error_longer', { what: t('password'), count: '8' }))
      .max(100, t('error_too_long'))
      .required(t('error_required', { what: t('new_password') })),
    confirm_new_password: Yup.string()
      .oneOf([Yup.ref('new_password'), null], t('error_match'))
      .required(t('error_required', { what: t('new_password_confirm') })),
  });

export const SiteSuppliersSchema = (t) =>
  Yup.object().shape({
    name: Yup.string()
      .min(1, t('error_longer', { what: t('name'), count: '1' }))
      .required(t('error_required', { what: t('name') })),
  });

export const CompanySchema = (t) =>
  Yup.object().shape({
    name: Yup.string()
      .trim()
      .required(t('error_required', { what: t('name') })),
    parent_company_id: Yup.number().nullable(),
  });

export const AlertRuleSchema = (t) =>
  Yup.object().shape({
    name: Yup.string()
      .trim()
      .required(t('error_required', { what: t('name') })),
    alert_name: Yup.string()
      .trim()
      .required(t('error_required', { what: t('alert_name') })),
    category: Yup.string()
      .oneOf(['ENVIRONMENTAL', 'MORTALITY', 'REHS'])
      .required(t('error_required', { what: t('alert_rule_category') })),
  });

// Used standalone by the edit page's alert-name section (a separate PATCH, not a new version).
export const AlertRuleAlertNameSchema = (t) =>
  Yup.object().shape({
    alert_name: Yup.string()
      .trim()
      .required(t('error_required', { what: t('alert_name') })),
  });

// scope_def is shape-level only (non-empty include) - the backend is authoritative for the
// rest (returned as array-of-strings errors, see FieldErrorList). conditions is additionally
// checked against alert_rule_schema.json: the builder can't construct an invalid leaf today,
// but this catches it if that ever stops being true, without waiting for a round trip.
export const AlertRuleVersionSchema = (t) =>
  Yup.object().shape({
    severity: Yup.string()
      .oneOf(['HIGH', 'MEDIUM', 'INFO'])
      .required(t('error_required', { what: t('alert_rule_severity') })),
    clear_consecutive_ok: Yup.number()
      .integer()
      .min(1, t('error_min_value', { what: t('clear_consecutive_ok'), count: '1' }))
      .required(t('error_required', { what: t('clear_consecutive_ok') })),
    scope_def: Yup.object().shape({
      include: Yup.array().min(1, t('error_alert_rule_scope_include_required')),
      exclude: Yup.array(),
    }),
    conditions: Yup.object()
      .required(t('error_alert_rule_conditions_required'))
      .test(
        'matches-condition-schema',
        t('error_alert_rule_conditions_invalid'),
        (value) => !value || validateConditionsSchema(value),
      ),
  });

// audience_mode/target_users are edited independently of scope_def/conditions (a separate PATCH
// on the backend, see backend/alerting README "Alert rule audience modes") - this schema is used
// standalone by the Audience tab, and concatenated into AlertRuleCreateSchema below.
export const AlertRuleAudienceSchema = (t) =>
  Yup.object().shape({
    audience_mode: Yup.string()
      .oneOf(['SITE_WIDE', 'SITE_USERS', 'SITE_MANAGERS', 'SITE_ADMINS', 'EXPLICIT_USERS', 'NONE'])
      .required(t('error_required', { what: t('audience_mode') })),
    target_users: Yup.array().when('audience_mode', {
      is: 'EXPLICIT_USERS',
      then: Yup.array().min(1, t('error_alert_rule_target_users_required')),
      otherwise: Yup.array().max(0),
    }),
  });

// Empty means no grouping; the backend accepts null or 10-120.
export const AlertRuleNotificationGroupSchema = (t) =>
  Yup.object().shape({
    notification_group_minutes: Yup.number()
      .transform((value, originalValue) => (originalValue === '' ? null : value))
      .nullable()
      .integer(t('error_notification_group_minutes_range'))
      .min(10, t('error_notification_group_minutes_range'))
      .max(120, t('error_notification_group_minutes_range')),
  });

// Rule creation posts both the rule (name) and its first version (severity/scope/conditions)
// in a single call, so the create form validates both schemas at once.
export const AlertRuleCreateSchema = (t) =>
  AlertRuleSchema(t)
    .concat(AlertRuleVersionSchema(t))
    .concat(AlertRuleAudienceSchema(t))
    .concat(AlertRuleNotificationGroupSchema(t));

export const OrderSchema = (t) =>
  Yup.object().shape({
    supplier: Yup.string()
      .required(t('error_required', { what: t('supplier') }))
      .nullable(),

    order_items: Yup.array()
      .of(
        Yup.object().shape({
          license_template_id: Yup.string()
            .required(t('error_required', { what: t('license_template_id') }))
            .nullable(),
        }),
      )
      .required(t('order_items')),
  });

export const BulkStatusSchema = (t) =>
  Yup.object().shape({
    delivery_status: Yup.string()
      .required(t('error_required', { what: t('target_status') }))
      .nullable(),

    deviceCodes: Yup.string()
      .required(t('error_required', { what: t('device_codes') }))
      .nullable(),
  });

export const TreatmentSchema = (t, { withReasonTypeOptions = false } = {}) => {
  if (!withReasonTypeOptions) {
    return Yup.object().shape({
      description: Yup.string()
        .required(t('error_required', { what: t('description') }))
        .trim(),
    });
  }

  return Yup.object({
    description: Yup.string().trim(),
    treatment_reason_id: Yup.number().nullable(),
    treatment_type_id: Yup.number().nullable(),
  }).test('treatment-content', t('error_treatment_requires_content'), function (values) {
    const hasDescription = !!values.description?.trim();
    const hasReason = values.treatment_reason_id != null;
    const hasType = values.treatment_type_id != null;
    if (hasDescription || hasReason || hasType) {
      return true;
    }
    return this.createError({ path: 'description', message: t('error_treatment_requires_content') });
  });
};

export const StartBatchFirstSchema = (t, options = {}) => {
  const { requireSource = false } = options;
  return Yup.object().shape({
    name: Yup.string()
      .max(40, t('error_too_long'))
      .required(t('error_required', { what: t('name') }))
      .trim(),
    start_date: Yup.date().required(t('start_date_required_message')).nullable(),
    start_age_in_days: Yup.number()
      .transform((value, originalValue) => (originalValue === '' ? undefined : value))
      .nullable()
      .optional()
      .typeError(t('error_only_numbers_positive'))
      .integer(t('error_only_numbers_positive'))
      .min(1, t('error_only_numbers_positive')),
    animal_source_type: Yup.string()
      .nullable()
      .when([], {
        is: () => requireSource,
        then: (schema) => schema.required(t('error_required', { what: t('animal_source_type') })),
        otherwise: (schema) => schema,
      }),
    animal_source_value: Yup.mixed()
      .nullable()
      .when('animal_source_type', (animalSourceType, schema) => {
        if (!requireSource) return schema;
        if (animalSourceType === 'other') {
          return Yup.string()
            .nullable()
            .transform((value) => (typeof value === 'string' ? value.trim() : value))
            .required(t('error_required', { what: t('animal_source_value') }));
        }
        if (animalSourceType === 'airspace' || animalSourceType === 'site') {
          return schema.required(t('error_required', { what: t('animal_source_value') }));
        }
        return schema;
      }),
  });
};

export const StartBatchSecondSchema = (t) =>
  Yup.object().shape({
    airspaces: Yup.array()
      .of(Yup.number())
      .min(1, t('error_add_airspace'))
      .required(t('error_required', { what: t('airspace') })),
  });

export const StartBatchThirdSchema = (t) =>
  Yup.object().shape({
    animal_amounts: Yup.object().test('valid-amounts', t('error_invalid_amount'), function (value) {
      const { airspaces } = this.parent;
      if (!airspaces || airspaces.length === 0) return true;

      const innerErrors = [];
      airspaces.forEach((id) => {
        const v = value?.[id];
        if (v === null || v === undefined || v === '') {
          innerErrors.push(new Yup.ValidationError(t('error_required_simple'), v, `animal_amounts.${id}`));
        } else if (!Number.isInteger(Number(v)) || Number(v) <= 0) {
          innerErrors.push(new Yup.ValidationError(t('error_must_be_greater_than_0'), v, `animal_amounts.${id}`));
        }
      });

      if (innerErrors.length > 0) {
        return new Yup.ValidationError(innerErrors, value, 'animal_amounts');
      }
      return true;
    }),
  });

export const BatchEditSchema = (t) =>
  Yup.object().shape({
    name: Yup.string()
      .max(40, t('error_too_long'))
      .required(t('error_required', { what: t('batch_name') }))
      .trim(),
    start_age_in_days: Yup.number()
      .transform((value, originalValue) => (originalValue === '' ? undefined : value))
      .nullable()
      .optional()
      .typeError(t('error_only_numbers_positive'))
      .integer(t('error_only_numbers_positive'))
      .min(1, t('error_only_numbers_positive')),
  });

export const WeChatRegistrationSchema = (t) =>
  Yup.object().shape({
    first_name: Yup.string()
      .trim()
      .min(1, t('error_longer', { what: t('first_name'), count: '1' }))
      .max(30, t('error_too_long'))
      .required(t('error_required', { what: t('first_name') }))
      .matches(nameRegEx, t('error_wrong_characters')),
    last_name: Yup.string()
      .trim()
      .min(1, t('error_longer', { what: t('last_name'), count: '1' }))
      .max(150, t('error_too_long'))
      .required(t('error_required', { what: t('last_name') }))
      .matches(nameRegEx, t('error_wrong_characters')),
    email: Yup.string()
      .email(t('error_invalid', { what: t('email_address') }))
      .max(150, t('error_too_long'))
      .nullable(), // Email is optional for WeChat users
    telephone: Yup.string()
      .min(3, t('error_longer', { what: t('telephone'), count: '3' }))
      .max(20, t('error_too_long'))
      .nullable(), // Telephone is optional for WeChat and China config
    timezone: Yup.string()
      .nullable()
      .required(t('error_required', { what: t('timezone') })),
    language: Yup.string()
      .nullable()
      .required(t('error_required', { what: t('language') })),
    country: Yup.string()
      .nullable()
      .required(t('error_required', { what: t('country') })),
  });
